const AUDIO_BASE_PATH = '/sounds/';

class AudioCacheManager {
  constructor() {
    // Map<itemId, HTMLAudioElement> – popolata da preloadFromCatalog()
    this._cache = new Map();

    // Map<itemId, string> – object URL dei blob (per revoke al dispose)
    this._objectURLs = new Map();

    // Map<itemId, Promise> – evita fetch doppie per lo stesso file
    this._inflight = new Map();
  }

  /**
   * Scansiona il catalogo, trova tutti gli item con campo "audio"
   * e li scarica in parallelo convertendoli in object URL
   *
   * @param {object} catalog – stesso oggetto JSON caricato da FurnitureController
   */
  async preloadFromCatalog(catalog) {
    if (!catalog?.categories) return;

    const toLoad = [];

    for (const category of catalog.categories) {
      for (const item of category.items) {
        if (item.audio && !this._cache.has(item.id) && !this._inflight.has(item.id)) {
          toLoad.push({ id: item.id, src: item.audio, name: item.name });
        }
      }
    }

    if (toLoad.length === 0) return;

    console.log(`[Audio] ⏳ Preload: ${toLoad.length} file audio...`);

    const results = await Promise.allSettled(
      toLoad.map(({ id, src, name }) => this._fetchAndCache(id, src, name))
    );

    const ok     = results.filter(r => r.status === 'fulfilled').length;
    const failed = results.filter(r => r.status === 'rejected').length;
    console.log(`[Audio] ✓ Preload completato: ${ok} ok, ${failed} errori.`);
  }

  /**
   * Scarica il file come Blob, crea un object URL e istanzia Audio
   * Se il fetch è già in corso attende la Promise esistente
   */
  async _fetchAndCache(id, src, name) {
    // Deduplicazione in-flight
    if (this._inflight.has(id)) {
      return this._inflight.get(id);
    }

    const promise = (async () => {
      try {
        const response = await fetch(AUDIO_BASE_PATH + src);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const blob      = await response.blob();
        const objectURL = URL.createObjectURL(blob);

        const audio = new Audio(objectURL);
        // preload:'auto' qui non serve perché i dati sono già locali,
        // ma lo imposto per coerenza con eventuali fallback del browser
        audio.preload = 'auto';

        this._cache.set(id, audio);
        this._objectURLs.set(id, objectURL);

      } catch (err) {
        console.warn(`[Audio] ✗ Preload fallito per "${name}" (${src}): ${err.message}`);
        throw err;
      } finally {
        this._inflight.delete(id);
      }
    })();

    this._inflight.set(id, promise);
    return promise;
  }

  /**
   * Riproduce l'audio associato all'itemId
   * Se l'audio è ancora in corso la richiesta viene ignorata
   * Se l'audio non è in cache (preload fallito o non configurato) non fa nulla
   *
   * @param {string} itemId   – catalogItem.id
   * @param {string} itemName – usato solo per il log
   * @returns {boolean} true se la riproduzione è partita
   */
  play(itemId, itemName = itemId) {
    const audio = this._cache.get(itemId);
    if (!audio) return false;

    if (audio.paused || audio.ended) {
      // Ferma qualsiasi altro audio in riproduzione prima di avviarne uno nuovo
      this._cache.forEach((other, otherId) => {
        if (otherId !== itemId && !other.paused) {
          other.pause();
          other.currentTime = 0;
        }
      });

      audio.currentTime = 0;
      audio.play().catch(err => {
        console.warn(`[Audio] "${itemName}" non riprodotto (autoplay policy?): ${err.message}`);
      });
      console.log(`🔊 ${itemName} – audio avviato`);
      return true;
    }

    console.log(`🔊 ${itemName} – audio già in riproduzione`);
    return false;
  }

  /**
   * Ritorna true se il catalogo definisce un audio per questo itemId
   * Utile per sapere se il doppio click deve fare qualcosa
   */
  has(itemId) {
    return this._cache.has(itemId);
  }

  // Libera tutte le risorse: ferma gli audio, revoca gli object URL
  dispose() {
    this._cache.forEach((audio, id) => {
      audio.pause();
      audio.src = '';
    });
    this._cache.clear();

    this._objectURLs.forEach(url => URL.revokeObjectURL(url));
    this._objectURLs.clear();

    this._inflight.clear();
    console.log('[Audio] ✓ Cache audio liberata.');
  }
}

// SINGLETON – condiviso tra FurnitureController e InputController
export const audioCacheManager = new AudioCacheManager();
export default AudioCacheManager;